import {
  afterRenderEffect,
  Component,
  computed,
  effect,
  ElementRef,
  HostListener,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { TextCleanPipe } from '../pipes/text-clean-pipe';
import { ChatErrorKind } from '../services/api/chat';
import { isFlutterWebView, readSafeAreaInset } from '../services/flutter-webview';
import { focusableElements, trapTab } from '../services/focus-trap';
import { MessageBridgeService } from '../services/message-bridge';
import { ResponsiveService } from '../services/responsive';
import {
  CHAT_MAX_QUESTION_CHARS,
  CHAT_SUGGESTED_QUESTIONS,
  ChatExchange,
  ChatQuestionSource,
  usedSourceLabels,
  V3ChatService,
} from '../services/v3-chat';
import { V3ChatTrigger } from './v3-chat-trigger';

const ERROR_TEXTS: Record<ChatErrorKind, string> = {
  unavailable: 'Der AI Chat ist in dieser Umgebung noch nicht verfügbar.',
  unauthorized: 'Deine Sitzung ist abgelaufen. Bitte öffne die AI Analyse erneut.',
  invalid: 'Die Frage konnte nicht verarbeitet werden. Bitte formuliere sie kürzer oder anders.',
  timeout: 'Die Antwort hat zu lange gedauert. Bitte versuche es noch einmal.',
  failed: 'Da ist etwas schiefgelaufen. Bitte versuche es noch einmal.',
};

// The AI chat panel (AI-664) plus the Flutter floating trigger; mounted by Core, V3 only. See CLAUDE.md here.
@Component({
  selector: 'app-v3-chat-widget',
  standalone: true,
  imports: [V3ChatTrigger, TextCleanPipe],
  templateUrl: './v3-chat-widget.html',
  styleUrl: './v3-chat-widget.scss',
})
export class V3ChatWidget {
  readonly chat = inject(V3ChatService);
  private readonly messageBridge = inject(MessageBridgeService);
  private readonly responsive = inject(ResponsiveService);

  readonly isFlutter = isFlutterWebView();
  // Mobile web is already full width and the Flutter sheet full screen.
  readonly canExpand = computed(() => !this.isFlutter && !this.responsive.isMobile());
  readonly safeAreaTop = readSafeAreaInset('safeAreaTop');
  readonly safeAreaBottom = readSafeAreaInset('safeAreaBottom');
  readonly suggestions = CHAT_SUGGESTED_QUESTIONS;
  readonly maxChars = CHAT_MAX_QUESTION_CHARS;

  readonly draft = signal('');
  readonly canSend = computed(() => this.draft().trim().length > 0 && !this.chat.pending());
  readonly placeholder = computed(() => {
    const name = this.chat.companyName();
    return name ? `Frage zu ${name} stellen …` : 'Deine Frage …';
  });

  private readonly dialog = viewChild<ElementRef<HTMLElement>>('dialog');
  private readonly textarea = viewChild<ElementRef<HTMLTextAreaElement>>('textarea');
  private readonly scroller = viewChild<ElementRef<HTMLElement>>('scroller');
  private backdropPressed = false;

  constructor() {
    // Web: the overlay is top-anchored and only the parent scrolls. Flutter: the WebView scrolls, lock it.
    effect((onCleanup) => {
      if (!this.chat.isOpen()) return;
      if (this.isFlutter) {
        document.body.style.overflow = 'hidden';
        onCleanup(() => (document.body.style.overflow = ''));
      } else {
        this.messageBridge.postMessage('SCROLL_TOP');
        window.scrollTo({ top: 0, behavior: 'auto' });
      }
    });

    // Focus the input on web; in Flutter the first button (close), so opening doesn't pop the
    // keyboard and Shift+Tab wraps inside (the trap only wraps from a focusable, not the dialog).
    afterRenderEffect(() => {
      const dialog = this.dialog()?.nativeElement;
      if (!dialog) return;
      const target = this.isFlutter ? focusableElements(dialog)[0] : this.textarea()?.nativeElement;
      (target ?? dialog).focus({ preventScroll: true });
    });

    // Scroll the newest message to where the intro sits, so a long answer reads from its start.
    afterRenderEffect(() => {
      this.chat.exchanges();
      const scroller = this.scroller()?.nativeElement;
      if (!scroller) return;
      const messages = scroller.querySelectorAll<HTMLElement>('.msg');
      const newest = messages[messages.length - 1];
      if (newest) scroller.scrollTop = newest.offsetTop - messages[0].offsetTop;
    });
  }

  // A click that both starts and ends on the backdrop closes the panel (a drag out of it doesn't).
  @HostListener('pointerdown', ['$event'])
  onPointerDown(event: PointerEvent): void {
    this.backdropPressed = this.isBackdrop(event.target);
  }

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent): void {
    if (this.backdropPressed && this.isBackdrop(event.target)) this.chat.close();
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    const dialog = this.dialog()?.nativeElement;
    if (!dialog) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.chat.close();
    } else if (event.key === 'Tab') {
      trapTab(dialog, event);
    }
  }

  onInput(event: Event): void {
    const el = event.target as HTMLTextAreaElement;
    this.draft.set(el.value);
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }

  // Enter sends, Shift+Enter breaks the line.
  onInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
      event.preventDefault();
      this.submit();
    }
  }

  onSubmit(event: Event): void {
    event.preventDefault();
    this.submit();
  }

  ask(question: string, source: ChatQuestionSource): void {
    void this.chat.ask(question, source);
  }

  errorText(kind: ChatErrorKind | null): string {
    return ERROR_TEXTS[kind ?? 'failed'];
  }

  canRetry(kind: ChatErrorKind | null): boolean {
    return kind !== 'unavailable' && kind !== 'invalid';
  }

  notes(exchange: ChatExchange): string[] {
    const response = exchange.response;
    if (!response) return [];
    const notes: string[] = [];
    if (response.unresolved_companies.length) {
      notes.push(`Nicht zugeordnet: ${response.unresolved_companies.join(', ')}`);
    }
    if (response.unanswerable_parts.length) {
      notes.push(`Keine Daten zu: ${response.unanswerable_parts.join(', ')}`);
    }
    return notes;
  }

  metaText(exchange: ChatExchange): string {
    const labels = exchange.response ? usedSourceLabels(exchange.response) : [];
    return labels.length ? `Quellen: ${labels.join(', ')}` : '';
  }

  private isBackdrop(target: EventTarget | null): boolean {
    return target instanceof HTMLElement && target.classList.contains('chat-overlay');
  }

  private submit(): void {
    if (!this.canSend()) return;
    const question = this.draft();
    this.draft.set('');
    const el = this.textarea()?.nativeElement;
    if (el) el.style.height = 'auto';
    void this.chat.ask(question, 'typed');
  }
}
